import { Combinations } from '@givebacks/utils/types';
import { useQuery } from '@tanstack/react-query';

import { storeItemWithCrowdfundingType } from '../../../temporary/storeItemWithCrowdfundingType';
import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { StoreItem, StoreItemResponse, StoreItemWithOptionalInventorySummary } from '../../types';
import { InventoryResponse } from '../../types/inventory';

const useShowConsumerStoreItem = <T = StoreItemWithOptionalInventorySummary>({
  causeId,
  uuid,
  select,
  include,
  _enabled = true,
}: {
  causeId?: string;
  uuid?: string;
  _enabled?: boolean;
  select?: (data: StoreItem) => T;
  include?: Combinations<'inventory_summary'>;
}) => {
  const StoreApi = useStoreApi();

  const getItem = async (context: {
    meta?: {
      causeId?: string;
      uuid?: string;
      include?: Combinations<'inventory_summary'>;
    };
  }) => {
    const { causeId, uuid, include } = context.meta || {};

    // Throw to enable type safety because we know the query will not run unless uuid is defined
    if (!uuid || !causeId) throw new Error('uuid is required');

    const { data } = await StoreApi.get<StoreItemResponse<StoreItemWithOptionalInventorySummary>>(
      `/consumer/store_items/${uuid}`,
      {
        params: {
          cause_id: causeId,
          include,
        },
      }
    );

    return storeItemWithCrowdfundingType(data.store_item);
  };

  const queryKey = uuid && causeId ? storeKeys.id(causeId).consumer.items.id(uuid).detail : [];

  return useQuery({
    queryKey,
    queryFn: getItem,
    meta: { causeId, uuid, include },
    enabled: !!uuid && !!causeId && _enabled,
    select,
  });
};

type UseShowConsumerStoreItemInventoryViaOptionsProps = {
  causeId?: string;
  uuid?: string;
  options?: Record<string, string>;
  enabled?: boolean;
};

const useShowConsumerStoreItemInventoryViaOptions = ({
  causeId,
  uuid,
  options,
  enabled = true,
}: UseShowConsumerStoreItemInventoryViaOptionsProps) => {
  const StoreApi = useStoreApi();

  const getConsumerStoreItemInventory = async (context: {
    meta?: {
      causeId?: string;
      uuid?: string;
      options?: Record<string, string>;
    };
  }) => {
    const { causeId, uuid, options } = context.meta || {};

    // Transform options object to bracket notation for axios params
    const optionsParams = options
      ? Object.entries(options).reduce(
          (acc, [key, value]) => {
            acc[`options[${key}]`] = value;
            return acc;
          },
          {} as Record<string, string>
        )
      : {};

    const { data } = await StoreApi.get<InventoryResponse>(
      `/consumer/store_items/${uuid}/inventory`,
      {
        params: {
          cause_id: causeId,
          uuid,
          ...optionsParams,
        },
      }
    );

    return data.inventory;
  };

  const queryKey =
    !!uuid && !!causeId
      ? [...storeKeys.id(causeId).consumer.items.id(uuid).inventory, options]
      : [];

  return useQuery({
    queryKey,
    queryFn: getConsumerStoreItemInventory,
    meta: { causeId, uuid, options },
    enabled: !!causeId && !!uuid && !!options && enabled,
  });
};

export { useShowConsumerStoreItem, useShowConsumerStoreItemInventoryViaOptions };
